import React from 'react';
import { MessageCircle, Plus, Users, Radio, User, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

function getConvIcon(conv) {
  if (conv.type === 'tovi') return <Sparkles size={16} className="text-amber-500" />;
  if (conv.type === 'broadcast') return <Radio size={16} className="text-amber-600" />;
  if (conv.type === 'group') return <Users size={16} className="text-indigo-600" />;
  return <User size={16} className="text-blue-600" />;
}

export function ConversationList({ conversations, toviConv, userId, onOpenChat, onNewChat, getConvDisplayName }) {
  return (
    <>
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-blue-600 to-indigo-600">
        <h3 className="text-sm font-bold text-white">Team Chat</h3>
        <Button data-testid="chat-new-btn" variant="ghost" size="sm" onClick={onNewChat} className="text-white hover:bg-white/20 h-7 text-xs px-2">
          <Plus size={14} className="mr-1" />New
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {/* Tovi pinned at top */}
        {toviConv && (
          <button
            data-testid="tovi-conv-pin"
            onClick={() => onOpenChat(toviConv)}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-amber-50 transition-colors text-left border-b border-amber-100 bg-gradient-to-r from-amber-50/60 to-orange-50/40"
          >
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-sm">
              <Sparkles size={16} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <p className="text-sm font-bold text-amber-900">Tovi</p>
                  <span className="text-[9px] font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">AI</span>
                </div>
                {toviConv.last_message_at && (
                  <span className="text-[10px] text-slate-400 flex-shrink-0 ml-2">
                    {new Date(toviConv.last_message_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500 truncate">{toviConv.last_message || 'Your personal AI assistant'}</p>
                {toviConv.unread_count > 0 && (
                  <span className="ml-2 w-5 h-5 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center flex-shrink-0">
                    {toviConv.unread_count > 9 ? '9+' : toviConv.unread_count}
                  </span>
                )}
              </div>
            </div>
          </button>
        )}

        {conversations.length === 0 && !toviConv ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-2 px-6 text-center">
            <MessageCircle size={32} className="text-slate-300" />
            <p className="text-sm font-semibold">No conversations yet</p>
            <p className="text-xs">Start a chat with a team member!</p>
          </div>
        ) : conversations.map(c => (
          <button
            key={c.id}
            data-testid={`conv-${c.id}`}
            onClick={() => onOpenChat(c)}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors text-left border-b border-slate-50"
          >
            <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
              {getConvIcon(c)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-blue-900 truncate">{getConvDisplayName(c)}</p>
                {c.last_message_at && (
                  <span className="text-[10px] text-slate-400 flex-shrink-0 ml-2">
                    {new Date(c.last_message_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500 truncate">{c.last_message || 'No messages yet'}</p>
                {c.unread_count > 0 && (
                  <span className="ml-2 w-5 h-5 bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center flex-shrink-0">
                    {c.unread_count > 9 ? '9+' : c.unread_count}
                  </span>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    </>
  );
}
